'use client'

import { TableSize } from '@/components/common/table-size'
import { Tabs } from '@/components/ui/tabs'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'
import { SIZE_DATA } from '../../../../../../lib/constants/size'
import { BodyInfo } from './body-info'
import { CalculateSize } from './caculate-size'
import { GuideLegging } from './guide-legging'
import { ProductSizeInfo } from './product-size-info'

type Props = {
  sizeChart: string
  isOnlyTable?: boolean
  shapeType?: {
    value: string
    label: string
    is_custom: boolean
  }
}

const EXCLUDED_SIZE_CHARTS = [
  '691be073e23ee3205b5e2dd0',
  '691fd178b11a865d5b0b683b',
]

export const SizeTableContent = ({
  sizeChart,
  isOnlyTable,
  shapeType,
}: Props) => {
  const sizeData = SIZE_DATA[sizeChart]
  const [sizeTab, setSizeTab] = useState('product-info')
  const t = useTranslations('product_detail')
  const tSizeChart = useTranslations()

  const isBraOrLegging = useMemo(
    () => ['BRA', 'LEGGING'].includes(sizeChart),
    [sizeChart]
  )

  const options = useMemo(() => {
    const tabs = [
      {
        label: t('title.specifications'),
        value: 'product-info',
        content: <ProductSizeInfo />,
      },
    ]

    // Chỉ thêm tab thông số cơ thể nếu không phải BRA hoặc LEGGING
    if (!isBraOrLegging) {
      tabs.push({
        label: t('instruction_size.body_specifications'),
        value: 'body-info',
        content: <BodyInfo sizeChart={sizeChart} />,
      })
    }

    return tabs
  }, [isBraOrLegging, sizeChart, t])

  return (
    <div className='mt-2 pb-15'>
      {shapeType?.label && shapeType?.label !== '(Không thiết lập)' ? (
        <div className='mb-2 rounded bg-neutral-100 px-4 py-3 text-xs md:text-sm'>
          {t('instruction_size.shape_type_label')}{' '}
          <span className='text-sm font-medium md:text-base'>
            {shapeType.label}
          </span>
        </div>
      ) : null}

      {!isOnlyTable && !EXCLUDED_SIZE_CHARTS.includes(sizeChart) ? (
        <CalculateSize sizeChart={sizeChart} />
      ) : null}

      {sizeData && (
        <>
          <TableSize
            rows={sizeData.rows.map((row) => ({
              ...row,
              name: tSizeChart(row.name),
            }))}
            columns={sizeData.columns}
            matrix={sizeData.matrix}
            title={t('instruction_size.body_specifications')}
            headerTitle={t(sizeData.headerTitle)}
            leftTitle={t(sizeData.leftTitle)}
            className='mt-8'
            suffixCol={t('instruction_size.form.unit.cm').toUpperCase()}
          />
        </>
      )}
      <div className='my-8 rounded bg-neutral-200 px-4 py-3 font-sans text-sm leading-4.5 text-neutral-800/90'>
        <p className='font-bold'>{t('instruction_size.notes.title')}</p>
        <ul className='list-disc pl-4'>
          <li>
            {t('instruction_size.notes.height_range', { min: 145, max: 175 })}
          </li>
          <li>{t('instruction_size.notes.unit_cm')}</li>
          <li>{t('instruction_size.notes.refer_size_table')}</li>
        </ul>
      </div>
      <div>
        {options.length > 1 ? (
          <Tabs
            options={options}
            onChange={(value) => setSizeTab(value)}
            defaultValue={sizeTab}
            className='h-fit border-b border-b-neutral-200 pb-0'
            triggerClassName='flex-1 border-b-[3px] font-semibold font-sans text-sm leading-4.5 px-4'
          />
        ) : (
          <ProductSizeInfo sizeChart={sizeChart} />
        )}

        {sizeChart === 'LEGGING' && <GuideLegging />}
      </div>
    </div>
  )
}
